import { type FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Check } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { Button } from '../components/ui/Button'
import { ErrorBox } from '../components/ui/Card'
import { inputClass } from '../components/ui/FormField'

export default function Login() {
  const { login, user, isLoading } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const resetSuccess = searchParams.get('reset') === 'success'

  const next = searchParams.get('next')
  const redirectTo = next && next.startsWith('/') ? next : '/dashboard'

  useEffect(() => {
    if (!isLoading && user) void navigate(redirectTo, { replace: true })
  }, [user, isLoading, navigate, redirectTo])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(email, password)
      void navigate(redirectTo, { replace: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Invalid credentials'
      if (msg === 'email_not_verified') {
        void navigate(`/verify-email?email=${encodeURIComponent(email)}`)
        return
      }
      setError(msg === 'Invalid credentials' ? 'Incorrect email or password.' : msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl shadow-card p-10 w-full max-w-md">
        <div className="text-center font-bold text-[1.35rem] text-accent mb-1">Vet VPMS</div>
        <div className="text-center text-text-3 text-[0.9rem] mb-8">Sign in to your practice</div>

        {resetSuccess && (
          <div className="bg-success-bg border border-success-border text-success-text rounded px-3 py-[0.65rem] text-sm mb-4 flex items-center gap-1">
            <Check size={14} strokeWidth={2.5} /> Password updated successfully. You can now sign in.
          </div>
        )}
        {error && <ErrorBox>{error}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)}>
          <label className="block text-sm font-semibold text-text-heading mb-[0.35rem]" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            className={`${inputClass(false)} mb-[1.1rem]`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            disabled={submitting}
          />

          <label className="block text-sm font-semibold text-text-heading mb-[0.35rem]" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            className={`${inputClass(false)} mb-[1.1rem]`}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={submitting}
          />

          <div className="flex justify-end -mt-3 mb-[1.1rem]">
            <Link to="/forgot-password" className="text-accent text-[0.825rem] no-underline hover:underline">
              Forgot password?
            </Link>
          </div>

          <Button type="submit" disabled={submitting} className="w-full mt-2">
            {submitting ? 'Signing in…' : 'Sign In'}
          </Button>
        </form>

        <div className="text-center mt-5 text-sm text-text-3">
          Don't have an account?{' '}
          <Link to="/register" className="text-accent font-semibold no-underline hover:underline">
            Create account
          </Link>
        </div>
      </div>
    </div>
  )
}
